﻿import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import type { TooltipBaseProps } from '../components';
import { Tooltip } from '../components';
import { usePersonalBeautyStore, usePersonalBeautyStoreUserInfo } from '../contexts';
import { ReferralType } from '../models';
import { ComponentWrapper } from './ComponentWrapper';

/**
 * Time after which will be tooltip automatically closed
 */
const AUTO_CLOSE_TIME = 5000; // ms

export interface TooltipContainerProps extends TooltipBaseProps {
  /**
   * If tooltip should close itself after period of time
   * @defaultValue true
   */
  autoClose?: boolean;
}

export const TooltipContainer: FC<TooltipContainerProps> = ({ onClick, autoClose = true, ...props }) => {
  const { canDisplayTooltip, setTooltipWasShown, storeInfo } = usePersonalBeautyStore();

  const { firstName, lastName, photoUrl } = usePersonalBeautyStoreUserInfo();

  // Custom callback, if user clicks tooltip then we trigger onClick and hide it
  const onClickCallback = useCallback<NonNullable<typeof onClick>>(
    (e) => {
      if (typeof onClick === 'function') {
        onClick(e);
      }
      // Just hide tooltip right away if user clicked
      setTooltipWasShown(true);
    },
    [onClick, setTooltipWasShown],
  );

  // Autoclose after defined time if conditions are met
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    // We only set timer if tooltip will render, was not shown before on device and if auto-close is enabled
    if (canDisplayTooltip && autoClose) {
      timer = setTimeout(() => setTooltipWasShown(true), AUTO_CLOSE_TIME);
    }

    return () => clearTimeout(timer);
  }, [canDisplayTooltip, autoClose, setTooltipWasShown]);

  if (!canDisplayTooltip) {
    return null;
  }

  return (
    <ComponentWrapper>
      <Tooltip
        {...props}
        referralType={storeInfo?.type ?? ReferralType.None}
        firstName={firstName}
        lastName={lastName}
        photoUrl={photoUrl}
        onClick={onClickCallback}
      />
    </ComponentWrapper>
  );
};
